import { useMutationWithToast } from './useQueryWithToast';
import { useStaticQuery } from './useOptimizedRealtimeQuery';
import { supabase } from '@/integrations/supabase/client';

export interface Stakeholder {
  id: string;
  assignment_id?: string;
  name: string;
  email?: string;
  phone?: string;
  role: string;
  source: 'manual' | 'system';
  user_id?: string;
  notes?: string;
  attachments?: any;
}

export interface TeamLead {
  id: string;
  user_id: string;
  name: string;
  email: string;
  avatar_url?: string;
}

// Hook to fetch stakeholders for a project via assignments
export const useProjectStakeholders = (projectId?: string) => {
  return useStaticQuery<Stakeholder[]>({
    queryKey: ['project-stakeholders', projectId],
    tableName: 'project_stakeholder_assignments',
    queryFn: async () => {
      if (!projectId) return [];
      
      const { data, error } = await supabase
        .from('project_stakeholder_assignments')
        .select(`
          id,
          role,
          stakeholders!project_stakeholder_assignments_stakeholder_id_fkey(
            id,
            name,
            email,
            phone,
            company,
            description,
            notes,
            attachments
          )
        `)
        .eq('project_id', projectId)
        .order('assigned_at', { ascending: true });

      if (error) throw error;

      return data.map(item => ({
        assignment_id: item.id,
        id: item.stakeholders?.id || '',
        name: item.stakeholders?.name || 'Unknown',
        email: item.stakeholders?.email,
        phone: item.stakeholders?.phone,
        role: item.role,
        source: 'manual' as const,
        user_id: undefined,
        notes: item.stakeholders?.notes,
        attachments: item.stakeholders?.attachments
      }));
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false, // useStaticQuery does NOT have this default!
  });
};

// Hook to fetch team leads for a project
export const useProjectTeamLeads = (projectId?: string) => {
  return useStaticQuery<TeamLead[]>({
    queryKey: ['project-team-leads', projectId],
    tableName: 'project_team_leads',
    queryFn: async () => {
      if (!projectId) return [];
      
      const { data, error } = await supabase
        .from('project_team_leads')
        .select(`
          id,
          user_id,
          profiles(
            full_name,
            email,
            avatar_url
          )
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return data.map(item => ({
        id: item.id,
        user_id: item.user_id,
        name: item.profiles?.full_name || 'Unknown',
        email: item.profiles?.email || '',
        avatar_url: item.profiles?.avatar_url
      }));
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false, // useStaticQuery does NOT have this default!
  });
};

// Hook to add stakeholder to project via assignment
export const useAddStakeholder = () => {
  return useMutationWithToast<any, {
    projectId: string;
    stakeholderId: string;
    role: string;
  }>({
    mutationFn: async ({ projectId, stakeholderId, role }) => {
      const { data, error } = await supabase
        .from('project_stakeholder_assignments')
        .insert({
          project_id: projectId,
          stakeholder_id: stakeholderId,
          role,
          assigned_by: null
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    successMessage: 'Đã thêm stakeholder thành công',
    invalidateQueries: [['stakeholders'], ['project-stakeholders'], ['project-stakeholders-admin']]
  });
};

// Hook to add team lead
export const useAddTeamLead = () => {
  return useMutationWithToast<any, {
    projectId: string;
    userId: string;
  }>({
    mutationFn: async ({ projectId, userId }) => {
      const { data, error } = await supabase
        .from('project_team_leads')
        .insert({
          project_id: projectId,
          user_id: userId
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    successMessage: 'Đã thêm team lead thành công',
    invalidateQueries: [['project-team-leads']]
  });
};

// Hook to remove stakeholder assignment
export const useRemoveStakeholder = () => {
  return useMutationWithToast<any, { id: string }>({
    mutationFn: async ({ id }) => {
      const { error } = await supabase
        .from('project_stakeholder_assignments')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    successMessage: 'Đã xóa stakeholder thành công',
    invalidateQueries: [['stakeholders'], ['project-stakeholders'], ['project-stakeholders-admin']]
  });
};

// Hook to remove team lead
export const useRemoveTeamLead = () => {
  return useMutationWithToast<any, { id: string }>({
    mutationFn: async ({ id }) => {
      const { error } = await supabase
        .from('project_team_leads')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    successMessage: 'Đã xóa team lead thành công',
    invalidateQueries: [['project-team-leads']]
  });
};